import { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';

import { InstructionsSentConatiner } from '../ui/instructions-sent-container';
import { ForgotPasswordContainer } from '../ui/forgot-password-container';
import { withErrorHandling } from '@selfserve/react-hocs';
import {
  StyledContentContainer,
  CommonLayout,
  StyledText,
} from '@selfserve/react-ui';
import { apiStatuses, getRecaptchaToken } from '@selfserve/react-utils';
import { useForgotPasswordRequest } from '../data';

export const StyledInfoText = styled(StyledText)`
  margin-bottom: 24px;
  font-family: Source Sans Pro;
`;

const validationSchema = (t: TFunction) =>
  Yup.object().shape({
    email: Yup.string()
      .email(t('common.validations.email-valid'))
      .required(t('common.validations.email-required')),
  });

export interface FormValues {
  email: string;
}

export const ForgotPassword = withErrorHandling(({ openErrorModal }) => {
  const { t, i18n } = useTranslation();
  const [validateOnChange, setValidateOnChange] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isInProccess, setIsInProccess] = useState(false);
  const {
    data: forgotPasswordResponse,
    mutateAsync: handleForgotPasswordRequest,
    isLoading,
  } = useForgotPasswordRequest();
  const formik: FormikProps<FormValues> = useFormik<FormValues>({
    validateOnChange,
    initialValues: { email: '' },
    validationSchema: validationSchema(t),
    onSubmit: ({ email: userEmail }) => {
      setIsInProccess(true);
      getRecaptchaToken()
        .then((recaptchaToken: string) => {
          handleForgotPasswordRequest({
            userEmail,
            recaptchaToken,
            postResetLink: window.location.origin,
          });
        })
        .catch(() => setIsInProccess(false));
    },
  });
  const { errors, setFieldError } = formik;

  useEffect(() => {
    const hasError = !!Object.keys(errors).length;
    if (hasError && !validateOnChange) {
      setValidateOnChange(true);
    } else if (!hasError) {
      setValidateOnChange(false);
    }
  }, [validateOnChange, errors]);

  useEffect(() => {
    const { data, status } = forgotPasswordResponse || {};
    if (status) {
      setIsInProccess(false);
      if (status === apiStatuses.BAD_REQUEST) {
        setFieldError(
          'email',
          i18n.exists(`errors.${data?.id}`)
            ? t(`errors.${data?.id}`)
            : data?.message_en
        );
      }
      if (status === apiStatuses.CREATED) {
        setIsSubmitted(true);
      }
    }
  }, [forgotPasswordResponse]);

  return (
    <CommonLayout showSideImage>
      <StyledContentContainer centered>
        {isSubmitted ? (
          <InstructionsSentConatiner />
        ) : (
          <ForgotPasswordContainer
            formik={formik}
            isLoading={isLoading || isInProccess}
          />
        )}
      </StyledContentContainer>
    </CommonLayout>
  );
});
